import React, { useEffect } from 'react'
import * as THREE from 'three'

const DEFAULT_SELECTORS = ['#main img.picture', '#main .picture img', '#main .heroImage', '#mail .heroImage']

const vertexShader = `
 varying vec2 vUv;
 void main() {
   vUv = uv;
   gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
 }
`

const fragmentShader = `
 uniform sampler2D uTexture;
 uniform vec2 uMouse;
 uniform float uTime;
 uniform float uStrength;
 varying vec2 vUv;

 float hash(vec2 p) {
   return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
 }

 void main() {
   vec2 m = clamp(uMouse, 0.0, 1.0);

   float d = distance(vUv, m);
   float ripple = sin(d * 24.0 - uTime * 4.0) * (0.010 * uStrength) * (1.0 - smoothstep(0.0, 0.7, d));

   vec2 dir = normalize(vUv - m + 1e-6);
   vec2 uv = vUv + dir * ripple;

   float n = hash(vUv * 120.0 + uTime * 0.05);
   uv += (n - 0.5) * 0.002 * uStrength;

   vec4 color = texture2D(uTexture, uv);
   gl_FragColor = color;
 }
`

function canRunOnThisDevice() {
  if (typeof window === 'undefined') return false
  if (window.innerWidth <= 768) return false
  if (typeof window.matchMedia !== 'function') return true
  return window.matchMedia('(hover:hover) and (pointer:fine)').matches
}

function pickTargetElements(selectors) {
  const result = []
  for (const selector of selectors) {
    document.querySelectorAll(selector).forEach((el) => result.push(el))
  }
  return result
}

function matchesAnySelector(el, selectors) {
  if (!(el instanceof Element)) return false
  for (const sel of selectors) {
    try {
      if (el.matches(sel)) return true
    } catch (e) {
      // ignore invalid selectors
    }
  }
  return false
}

function isNonLinkImage(imgEl) {
  if (!(imgEl instanceof Element)) return false
  return !imgEl.closest('a')
}

function isExcludedImage(imgEl) {
  if (!(imgEl instanceof Element)) return true
  if (imgEl.closest('.logo')) return true
  return false
}

export default function ImageLiquidHoverFX({ selectors = DEFAULT_SELECTORS, strength = 0.75, nonLinkOnly = true }) {
  useEffect(() => {
    if (!canRunOnThisDevice()) return

    let active = null

    const textureLoader = new THREE.TextureLoader()

    const activate = (imgEl) => {
      if (isExcludedImage(imgEl)) return
      if (nonLinkOnly && !isNonLinkImage(imgEl)) return
      if (active?.imgEl === imgEl) return
      deactivate()

      const canvasContainer = document.createElement('div')
      canvasContainer.style.position = 'fixed'
      canvasContainer.style.left = '0px'
      canvasContainer.style.top = '0px'
      canvasContainer.style.width = '0px'
      canvasContainer.style.height = '0px'
      canvasContainer.style.pointerEvents = 'none'
      canvasContainer.style.zIndex = '9999'

      document.body.appendChild(canvasContainer)

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
      renderer.domElement.style.width = '100%'
      renderer.domElement.style.height = '100%'
      renderer.domElement.style.display = 'block'
      canvasContainer.appendChild(renderer.domElement)

      const scene = new THREE.Scene()
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)

      const src = imgEl.currentSrc || imgEl.src
      const texture = textureLoader.load(src)
      texture.minFilter = THREE.LinearFilter
      texture.magFilter = THREE.LinearFilter
      texture.generateMipmaps = false

      const uniforms = {
        uTexture: { value: texture },
        uMouse: { value: new THREE.Vector2(0.5, 0.5) },
        uTime: { value: 0 },
        uStrength: { value: strength }
      }

      const material = new THREE.ShaderMaterial({
        uniforms,
        vertexShader,
        fragmentShader,
        transparent: true
      })

      const geometry = new THREE.PlaneGeometry(2, 2, 1, 1)
      const mesh = new THREE.Mesh(geometry, material)
      scene.add(mesh)

      const state = {
        imgEl,
        canvasContainer,
        renderer,
        scene,
        camera,
        geometry,
        material,
        texture,
        uniforms,
        raf: 0,
        lastRect: null
      }

      const syncRect = () => {
        const rect = imgEl.getBoundingClientRect()
        if (!rect.width || !rect.height) return false

        const w = Math.round(rect.width)
        const h = Math.round(rect.height)

        const prev = state.lastRect
        if (!prev || prev.left !== rect.left || prev.top !== rect.top || prev.width !== w || prev.height !== h) {
          state.lastRect = { left: rect.left, top: rect.top, width: w, height: h }
          canvasContainer.style.left = `${rect.left}px`
          canvasContainer.style.top = `${rect.top}px`
          canvasContainer.style.width = `${w}px`
          canvasContainer.style.height = `${h}px`
          renderer.setSize(w, h, false)
        }

        return true
      }

      const animate = (t) => {
        state.raf = window.requestAnimationFrame(animate)
        if (!syncRect()) return

        uniforms.uTime.value = t * 0.001
        renderer.render(scene, camera)
      }

      state.raf = window.requestAnimationFrame(animate)
      active = state
    }

    const deactivate = () => {
      if (!active) return

      window.cancelAnimationFrame(active.raf)

      try {
        active.texture.dispose()
        active.geometry.dispose()
        active.material.dispose()
        active.renderer.dispose()
      } catch (e) {
        // ignore
      }

      if (active.canvasContainer?.parentNode) {
        active.canvasContainer.parentNode.removeChild(active.canvasContainer)
      }

      active = null
    }

    const onMove = (e) => {
      if (!active) return
      const rect = active.imgEl.getBoundingClientRect()
      const x = (e.clientX - rect.left) / rect.width
      const y = (e.clientY - rect.top) / rect.height
      active.uniforms.uMouse.value.set(x, 1 - y)
    }

    const findTargetImage = (eventTarget) => {
      if (!(eventTarget instanceof Element)) return null

      const img = eventTarget.closest('img')
      if (!(img instanceof HTMLImageElement)) return null
      if (!matchesAnySelector(img, selectors)) return null
      return img
    }

    const onPointerOver = (e) => {
      const img = findTargetImage(e.target)
      if (!img) return
      activate(img)
    }

    const onPointerOut = (e) => {
      if (!active) return
      const from = e.target
      const to = e.relatedTarget
      if (!(from instanceof Element)) return
      if (to instanceof Element && to.closest('img') === active.imgEl) return
      if (from.closest('img') === active.imgEl) deactivate()
    }

    document.addEventListener('mousemove', onMove, { passive: true })
    document.addEventListener('pointerover', onPointerOver, true)
    document.addEventListener('pointerout', onPointerOut, true)

    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('pointerover', onPointerOver, true)
      document.removeEventListener('pointerout', onPointerOut, true)
      deactivate()
    }
  }, [selectors, strength, nonLinkOnly])

  return null
}
